'use client';

import { PageHeader } from '../../../components/shared/PageHeader';
import { formatCurrency } from '../../../types';
import { getBalanceSheetData } from '../data/mock-data';

export function BalanceSheetPage() {
  const data = getBalanceSheetData();

  return (
    <div className="space-y-6">
      <PageHeader
        title="대차대조표"
        description="재무상태표를 확인합니다."
      />

      {/* 기준일 */}
      <div className="p-4 rounded-lg bg-card border border-border">
        <div className="text-sm text-muted-foreground">기준일</div>
        <div className="text-xl font-bold mt-1">{data.asOf}</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 자산 */}
        <div className="p-6 rounded-lg bg-card border border-border">
          <h2 className="text-lg font-bold text-blue-600 dark:text-blue-400 mb-4">자산</h2>

          {/* 유동자산 */}
          <div className="mb-6">
            <h3 className="font-semibold text-muted-foreground mb-3">유동자산</h3>
            <div className="space-y-2">
              {data.assets.currentAssets.map((item) => (
                <div key={item.accountCode} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    <span className="font-mono mr-2">{item.accountCode}</span>
                    {item.accountName}
                  </span>
                  <span className="font-mono">{formatCurrency(item.amount)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-3 pt-2 border-t border-border font-medium">
              <span>유동자산 계</span>
              <span className="font-mono">{formatCurrency(data.assets.totalCurrentAssets)}</span>
            </div>
          </div>

          {/* 고정자산 */}
          <div className="mb-6">
            <h3 className="font-semibold text-muted-foreground mb-3">고정자산</h3>
            <div className="space-y-2">
              {data.assets.fixedAssets.map((item) => (
                <div key={item.accountCode} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    <span className="font-mono mr-2">{item.accountCode}</span>
                    {item.accountName}
                  </span>
                  <span className="font-mono">{formatCurrency(item.amount)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-3 pt-2 border-t border-border font-medium">
              <span>고정자산 계</span>
              <span className="font-mono">{formatCurrency(data.assets.totalFixedAssets)}</span>
            </div>
          </div>

          {/* 자산 총계 */}
          <div className="flex justify-between p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 font-bold text-blue-700 dark:text-blue-300">
            <span>자산 총계</span>
            <span className="font-mono">{formatCurrency(data.assets.totalAssets)}</span>
          </div>
        </div>

        {/* 부채 및 자본 */}
        <div className="space-y-6">
          {/* 부채 */}
          <div className="p-6 rounded-lg bg-card border border-border">
            <h2 className="text-lg font-bold text-red-600 dark:text-red-400 mb-4">부채</h2>

            {/* 유동부채 */}
            <div className="mb-6">
              <h3 className="font-semibold text-muted-foreground mb-3">유동부채</h3>
              <div className="space-y-2">
                {data.liabilities.currentLiabilities.map((item) => (
                  <div key={item.accountCode} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      <span className="font-mono mr-2">{item.accountCode}</span>
                      {item.accountName}
                    </span>
                    <span className="font-mono">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-3 pt-2 border-t border-border font-medium">
                <span>유동부채 계</span>
                <span className="font-mono">{formatCurrency(data.liabilities.totalCurrentLiabilities)}</span>
              </div>
            </div>

            {/* 장기부채 */}
            <div className="mb-6">
              <h3 className="font-semibold text-muted-foreground mb-3">장기부채</h3>
              <div className="space-y-2">
                {data.liabilities.longTermLiabilities.map((item) => (
                  <div key={item.accountCode} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      <span className="font-mono mr-2">{item.accountCode}</span>
                      {item.accountName}
                    </span>
                    <span className="font-mono">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-3 pt-2 border-t border-border font-medium">
                <span>장기부채 계</span>
                <span className="font-mono">{formatCurrency(data.liabilities.totalLongTermLiabilities)}</span>
              </div>
            </div>

            {/* 부채 총계 */}
            <div className="flex justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/20 font-bold text-red-700 dark:text-red-300">
              <span>부채 총계</span>
              <span className="font-mono">{formatCurrency(data.liabilities.totalLiabilities)}</span>
            </div>
          </div>

          {/* 자본 */}
          <div className="p-6 rounded-lg bg-card border border-border">
            <h2 className="text-lg font-bold text-purple-600 dark:text-purple-400 mb-4">자본</h2>

            <div className="space-y-2 mb-4">
              {data.equity.items.map((item) => (
                <div key={item.accountCode} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    <span className="font-mono mr-2">{item.accountCode}</span>
                    {item.accountName}
                  </span>
                  <span className="font-mono">{formatCurrency(item.amount)}</span>
                </div>
              ))}
            </div>

            {/* 자본 총계 */}
            <div className="flex justify-between p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 font-bold text-purple-700 dark:text-purple-300">
              <span>자본 총계</span>
              <span className="font-mono">{formatCurrency(data.equity.totalEquity)}</span>
            </div>
          </div>

          {/* 부채 및 자본 총계 */}
          <div className="p-4 rounded-lg bg-gray-100 dark:bg-gray-800">
            <div className="flex justify-between font-bold text-lg">
              <span>부채 및 자본 총계</span>
              <span className="font-mono">
                {formatCurrency(data.liabilities.totalLiabilities + data.equity.totalEquity)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 검증 */}
      <div className="p-4 rounded-lg bg-card border border-border">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">대차 균형 검증</span>
          {data.assets.totalAssets === data.liabilities.totalLiabilities + data.equity.totalEquity ? (
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
              ✓ 균형
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
              ✗ 불균형
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
